import "./sentry-init.js";
import "./styles.css";
import { getGameModeMeta } from "./config.js";
import { readSavedProgress } from "./progress.js";

const DEFAULT_MODAL_COPY = {
  eyebrow: "Welcome to Pathimo",
  title: "Let's play",
  rules: "Connect matching dots, keep every path tidy, and fill the whole board without crossing lines.",
};

const RESUME_MODAL_COPY = {
  eyebrow: "Welcome back",
  title: "Continue where you left off?",
  rules: "Your last board is tucked away safely. Pick it up again or start a fresh game.",
};

const refs = {
  startModal: document.getElementById("startModal"),
  startModalEyebrow: document.getElementById("startModalEyebrow"),
  startModalTitle: document.getElementById("startModalTitle"),
  startModalRules: document.getElementById("startModalRules"),
  startModalStatus: document.getElementById("startModalStatus"),
  startGameButton: document.getElementById("startGameButton"),
  continueGameButton: document.getElementById("continueGameButton"),
  gameModeSelect: document.getElementById("gameModeSelect"),
  gameModeDescription: document.getElementById("gameModeDescription"),
};

let gameInstancePromise = null;
let isLaunchingGame = false;

function applySelectedGameModeUi(gameMode = "standard") {
  const meta = getGameModeMeta(gameMode);
  document.body.dataset.gameMode = gameMode;
  document.body.dataset.audioVariant = meta.audioVariant;

  if (refs.gameModeDescription) {
    refs.gameModeDescription.textContent = meta.description;
  }
}

function updateStartModalUi() {
  const savedProgress = readSavedProgress();
  const hasSavedProgress = Boolean(savedProgress);
  const modalCopy = hasSavedProgress ? RESUME_MODAL_COPY : DEFAULT_MODAL_COPY;
  const selectedGameMode = savedProgress?.gameMode || refs.gameModeSelect?.value || "standard";

  if (refs.startModalEyebrow) {
    refs.startModalEyebrow.textContent = modalCopy.eyebrow;
  }

  if (refs.startModalTitle) {
    refs.startModalTitle.textContent = modalCopy.title;
  }

  if (refs.startModalRules) {
    refs.startModalRules.textContent = modalCopy.rules;
  }

  if (refs.continueGameButton) {
    refs.continueGameButton.hidden = !hasSavedProgress;
  }

  if (refs.startGameButton) {
    refs.startGameButton.textContent = hasSavedProgress ? "New game" : "Let's go";
    refs.startGameButton.classList.toggle("button--primary", !hasSavedProgress);
    refs.startGameButton.classList.toggle("button--secondary", hasSavedProgress);
  }

  if (refs.startModalStatus) {
    if (!hasSavedProgress) {
      refs.startModalStatus.hidden = true;
      refs.startModalStatus.textContent = "";
    } else {
      const gameModeLabel = getGameModeMeta(savedProgress.gameMode).label;
      refs.startModalStatus.hidden = false;
      refs.startModalStatus.textContent =
        `Saved progress found for Level ${savedProgress.levelNumber} in ${gameModeLabel} mode.`;
    }
  }

  if (savedProgress?.gameMode && refs.gameModeSelect) {
    refs.gameModeSelect.value = savedProgress.gameMode;
  }

  applySelectedGameModeUi(selectedGameMode);

  if (refs.startModal) {
    refs.startModal.hidden = false;
  }
}

function setLaunchPending(isPending) {
  isLaunchingGame = isPending;

  if (refs.startGameButton) {
    refs.startGameButton.disabled = isPending;
  }

  if (refs.continueGameButton) {
    refs.continueGameButton.disabled = isPending;
  }
}

async function loadGame() {
  if (!gameInstancePromise) {
    gameInstancePromise = import("./game.js").then(({ createGame }) => createGame(document));
  }

  return gameInstancePromise;
}

async function launchGame(runGame) {
  if (isLaunchingGame) {
    return;
  }

  setLaunchPending(true);

  try {
    const game = await loadGame();
    await runGame(game);
  } finally {
    setLaunchPending(false);
  }
}

refs.startGameButton?.addEventListener("click", () => {
  const selectedGameMode = refs.gameModeSelect?.value || "standard";
  void launchGame((game) => game.startNewGame(selectedGameMode));
});

refs.gameModeSelect?.addEventListener("change", (event) => {
  applySelectedGameModeUi(event.currentTarget.value);
});

refs.continueGameButton?.addEventListener("click", () => {
  const savedProgress = readSavedProgress();
  if (!savedProgress) {
    updateStartModalUi();
    return;
  }

  void launchGame((game) => game.continueSavedGame());
});

for (const button of [refs.startGameButton, refs.continueGameButton]) {
  button?.addEventListener("pointerenter", () => {
    void loadGame();
  }, { once: true });
  button?.addEventListener("focus", () => {
    void loadGame();
  }, { once: true });
}

updateStartModalUi();
